import styles from '../../../styles/Register.module.scss'
import techTalentStyles from '../../../styles/TechTalent.module.scss'
import React, { useRef, useContext, useState, useEffect } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
	Form,
	Row,
	Col,
	Image,
	Button,
	Spinner
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCamera } from '@fortawesome/free-solid-svg-icons'
import { useRouter } from 'next/router'

import UserContext from '../../../UserContext'
import validateNRIC from '../../../lib/validateNRIC'


export default function PersonalDetails ({ inputs, setInputs, datas, boolean, setBoolean, imgPreview, setImgPreview, setImgData, isMyinfoSuccess, setSchema }): JSX.Element {
	const router = useRouter()
	const { t } = useTranslation()
	const { gender, isLocale } = boolean

	const locale = router?.locale?.toLocaleUpperCase()

	const { user } = useContext(UserContext)

	const imgInputRef = useRef<HTMLInputElement>(null)
	const [nricError, setNricError]=useState('')
	// const languageArr = ['English', 'Chinese', 'Malay', 'Tamil']

	const onFileChange = (event): void => {
		const fileType = event.target.accept
		const file = event.target.files[0]

		// if User actually selected a file
		if (file !== undefined) {
			if (fileType === 'image/*') {
				setImgPreview(URL.createObjectURL(file))
				setImgData(file)
			}
		}
	}

	const handleImgPreview = (): string => {
		if (imgPreview === '') {
			return inputs.avatar
		} else return imgPreview
	}

	const handleNRIC = (event): void => {
		const str = event.target.value
		setInputs({ ...inputs, nric: str.toUpperCase() })
		const valid = validateNRIC(str)
		if (valid) {
			setNricError('')
		} else {
			setNricError('Please enter a valid NRIC')
		}
	}

	useEffect(() => {
		const personalDetailsSchema = Yup.object().shape({
			name: Yup.string()
				.required('Name is required')
				.min(2, 'Name must be at least 2 characters')
				.matches(/^[^`0-9~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]*$/, 'Name contains invalid characters'),
			email: Yup.string()
				.required('Email is required')
				.email('Invalid email format')
				.matches(/^[^`~!#$%^&*()_|+\-=?;:'",<>\{\}\[\]\\\/]*$/, 'Email contains invalid characters'),
			nric: Yup.string()
				.required('NRIC is required')
				.test('valid-nric', 'Please enter a valid NRIC', (value) => {
					if (!value) return false
					return validateNRIC(value.toUpperCase())
				}),
			gender: Yup.string()
				.required('Gender is required')
				.oneOf(['MALE', 'FEMALE', 'male', 'female'], 'Please select a valid gender'),
			dateOfBirth: Yup.date()
				.required('Date of birth is required')
				.max(new Date(Date.now() - 16 * 365 * 24 * 60 * 60 * 1000), 'Must be at least 16 years old'),
			isLocale: Yup.string()
				.required('Please select your nationality status'),
			locationMrt: locale === 'SG' ? Yup.string()
				.when('isLocale', {
					is: 'true',
					then: Yup.string().required('Location is required')
				}) : Yup.string(),
			locationCity: locale === 'MY' ? Yup.number().nullable()
				.when('isLocale', {
					is: 'true',
					then: Yup.number().required('Location is required')
				}) : Yup.number().nullable(),
			nationality: Yup.string()
				.when('isLocale', {
					is: 'false',
					then: Yup.string().required('Nationality is required')
				}),
			workPass: Yup.string().nullable(),
			whatsapp: Yup.string().nullable()
				.matches(/^[^a-zA-Z`~!@#$%^&*_|\-=?;:'",.<>\{\}\[\]\\\/]*$/, 'WhatsApp number contains invalid characters'),
			educationLevel: Yup.string()
				.required('Education level is required')
		})

		setSchema(personalDetailsSchema)
	}, [locale, setSchema])

	return (
		<Form
			onSubmit={event => {
				event.preventDefault()
				event.stopPropagation()
			}}
		>
			<Row className='mb-3'>
				<Col>
					<Form.Group className='mb-3'>
						<Form.Label>
							{t('update_profile.upload_picture')}
						</Form.Label>
						<input
							type='file'
							ref={imgInputRef}
							onChange={onFileChange}
							hidden
							accept='image/*'
						/>
						{/* Either render the button or the image */}
						{(imgPreview !== '' || (inputs.avatar !== '' && inputs.avatar !== null)) ? (
							<div
								className={styles.imageContainer}
								onClick={() => {
									if (imgInputRef !== null && imgInputRef.current !== null) {
										imgInputRef.current.click()
									}
								}}
							>
								<Image
									roundedCircle
									className={styles.avatarImage}
									src={handleImgPreview()}
									alt='avatar'
								/>
								<FontAwesomeIcon
									icon={faCamera}
									color='red'
									width={22}
									className={styles.imageIcon}
								/>
								<div className='mt-2'>
									<Button
										variant='link'
										className={techTalentStyles.link}
										size='sm'
										onClick={() => {
											if (imgInputRef !== null && imgInputRef.current !== null) {
												imgInputRef.current.click()
											}
										}}
									>
										{t('update_profile.change_avatar')}
									</Button>
								</div>
							</div>
						) : (
							<div
								className={styles.imageContainer}
							>
								<Image
									roundedCircle
									className={styles.avatarImage}
									src='/img/profile-placeholder.png'
									alt='avatar'
								/>
								<div className='mt-2'>
									<Button
										variant='link'
										className={techTalentStyles.link}
										size='sm'
										onClick={() => {
											if (imgInputRef !== null && imgInputRef.current !== null) {
												imgInputRef.current.click()
											}
										}}
									>
										{t('update_profile.upload_avatar')}
									</Button>
								</div>
							</div>
						)}
					</Form.Group>
				</Col>
			</Row>
			<Row className='mb-3'>
				<Col sm={12} md>
					<Form.Group className='mb-3'>
						<Form.Label>
							{t('update_profile.name')}
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<Form.Control
							className={`${styles.formControl} ${techTalentStyles.input}`}
							type='text'
							value={inputs.name}
							onChange={event => setInputs({ ...inputs, name: event.target.value })}
							required
							disabled={isMyinfoSuccess}
						/>
						{/* <Form.Control.Feedback type='invalid' /> */}
					</Form.Group>
				</Col>

				{user !== null && (
					<Col sm={12} md>
						<Form.Group>
							<Form.Label>
								{t('update_profile.email')}
								<span className={styles.mandatory}>*</span>
							</Form.Label>
							<Form.Control
								className={`${styles.formControl} ${techTalentStyles.input}`}
								type='email'
								value={inputs.email}
								onChange={event => setInputs({ ...inputs, email: event.target.value })}
								required
							/>
						</Form.Group>
					</Col>
				)}

			</Row>
			<Row className='mb-3'>
				<Col sm={12} md={6}>
					<Form.Group className='mb-3'>
						<Form.Label>
							{t('update_profile.nric')}
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<Form.Control
							className={`${styles.formControl} ${techTalentStyles.input}`}
							type='input'
							value={inputs.nric}
							onChange={handleNRIC}
							required
							disabled={isMyinfoSuccess}
						/>
						{nricError && (
							<div style={{ color: 'red', marginTop: '5px', fontSize: '14px' }}>
								{nricError}
							</div>
						)}
					</Form.Group>
				</Col>
				<Col sm={12} md={6}></Col>
			</Row>
			<Row className='mb-3'>
				<Col sm={12} md={6}>
					<Form.Group className='mb-3'>
						<Form.Label>
							{t('update_profile.gender')}
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<div>
							<Button
								variant={gender === 'male' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${gender === 'male' && techTalentStyles.primaryBtn}`}
								disabled={isMyinfoSuccess}
								onClick={() => {
									setBoolean({ ...boolean, gender: 'male' })
									setInputs({ ...inputs, gender: 'MALE' })
								}}
							>
								{t('update_profile.male')}
							</Button>
							<Button
								variant={gender === 'female' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${gender === 'female' && techTalentStyles.primaryBtn}`}
								disabled={isMyinfoSuccess}
								onClick={() => {
									setBoolean({ ...boolean, gender: 'female' })
									setInputs({ ...inputs, gender: 'FEMALE' })
								}}
							>
								{t('update_profile.female')}
							</Button>
						</div>
					</Form.Group>
				</Col>
				<Col sm={12} md={6}>
					<Form.Group>
						<Form.Label>
							{t('update_profile.dob')}
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<Form.Control
							className={`${styles.formControl} ${techTalentStyles.input}`}
							type='date'
							disabled={isMyinfoSuccess}
							value={inputs.dateOfBirth}
							onChange={event => {
								setInputs({
									...inputs,
									dateOfBirth: event.target.value
								})
							}}
							required
							min='1921-01-01'
							max={moment().subtract(16, 'years').format('YYYY-MM-DD')}
						/>
					</Form.Group>
				</Col>
			</Row>
			<Row className='mb-3'>
				{datas !== undefined ? (
					<Col sm={12} md={6}>
						<Form.Group className='mb-3'>
							<Form.Label>
								{t('update_profile.lives_in')}
								<span className={styles.mandatory}>*</span>
							</Form.Label>
							<Form.Select
								as='select'
								className={`${styles.formControl} ${techTalentStyles.input}`}
								value={locale === 'SG' ? inputs.locationMrt : locale === 'MY' ? inputs.locationCity : inputs.locationMrt}
								onChange={event => {
									locale === 'SG' ?
										setInputs({
											...inputs,
											locationMrt: event.target.value
										}) :
										locale === 'MY' ?
											setInputs({
												...inputs,
												locationCity: Number(event.target.value)
											}) :
											setInputs({
												...inputs,
												locationMrt: event.target.value
											})
								}}
								required
							>
								<option value='' disabled>{locale === 'SG' ? 'Select MRT' : locale === 'MY' ? 'Select city' : 'Select a train station'}</option>
								{
									locale === 'SG' ?
										datas.MrtStation.map((station, index) => (
											<option key={index} value={`${station.id} ${station.name}`}>
												{station.id} {station.name}
											</option>
										)) :
										locale === 'MY' ?
											datas.City.map((ct, index) => (
												<option key={index} value={`${ct.id}`}>
													{ct.name}
												</option>
											)) :
											datas.MrtStation.map((station, index) => (
												<option key={index} value={`${station.id} ${station.name}`}>
													{station.id} {station.name}
												</option>
											))
								}
								<option value='null'>{t('update_profile.not_in_singapore')}</option>
							</Form.Select>
						</Form.Group>
					</Col>
				) : <Col sm={12} md={6}><Spinner animation='border' /></Col>}
				<Col sm={12} md={6}>
					<Form.Group>
						<Form.Label>
							{locale === 'SG' ? 'Are you a Singaporean?' : locale === 'MY' ? 'Are you a Malaysian?' : 'Are you a Singaporean?' }
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<div>
							{/* <Form.Check inline label='Yes' type='radio' />
              <Form.Check inline label='No' type='radio' /> */}
							<Button
								variant={isLocale === 'true' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${isLocale === 'true' && techTalentStyles.primaryBtn}`}
								onClick={() => {
									setBoolean({ ...boolean, isLocale: 'true' })
									setInputs({ ...inputs, nationality: locale, workPass: 'null' })
								}}
							>
								{t('update_profile.yes')}
							</Button>
							<Button
								variant={isLocale === 'false' ? 'danger' : 'light'}
								className={`${styles.selectItem} ${isLocale === 'false' && techTalentStyles.primaryBtn}`}
								onClick={() => {
									setBoolean({ ...boolean, isLocale: 'false' })
									setInputs({ ...inputs, workPass: 'null', nationality: '' })
								}}
							>
								{t('update_profile.no')}
							</Button>
						</div>
					</Form.Group>
				</Col>
			</Row>
			
			<Row className='mb-3'>
				{datas !== undefined ? (<Col sm={12} md={6}>
					<Form.Group className='mb-3'>
						<Form.Label>
							{t('update_profile.nationality')}
							<span className={styles.mandatory}>*</span>
						</Form.Label>
						<Form.Control
							className={`${styles.formControl} ${techTalentStyles.input}`}
							as='select'
							value={inputs.nationality}
							disabled={isMyinfoSuccess}
							onChange={event => {
								setInputs({
									...inputs,
									nationality: event.target.value
								})
							}}
							required
						>
							<option value='' disabled>{t('update_profile.select_nationality')}</option>
							{datas.Enum_Nationality.map((nationality, index) => (
								<option key={index} value={nationality.alpha2}>
									{nationality.country}
								</option>
							))}
						</Form.Control>
					</Form.Group>
				</Col>
				) : <Row className='mb-3'><Col><Spinner animation='border' /></Col></Row>}
				{isLocale === 'false' && (
					<Col>
						<Form.Group>
							<Form.Label>
								{t('update_profile.work_pass')}
								<span className={styles.mandatory}>*</span>
							</Form.Label>
							<Form.Control
								className={`${styles.formControl} ${techTalentStyles.input}`}
								as='select'
								value={inputs.workPass === null ? 'null' : inputs.workPass}
								onChange={event => {
									setInputs({
										...inputs,
										workPass: event.target.value
									})
								}}
								required
							>
								<option value='' disabled>{t('update_profile.select_work_pass')}</option>
								{datas.Enum_WorkPass.map((workPass, index) => (
									<option key={index} value={workPass.workPass}>
										{workPass.display}
									</option>
								))}
								<option value='null'>{t('update_profile.dont_hold_work_pass')}</option>
							</Form.Control>
						</Form.Group>
					</Col>
				
				)}
			</Row>
			<Row className='mb-3'>
				<Col sm={12} md={6}>
					<Form.Group className='mb-3'>
						<Form.Label>
							{t('update_profile.wa_number')}
						</Form.Label>
						<Form.Control
							className={`${styles.formControl} ${techTalentStyles.input}`}
							type='tel'
							value={inputs.whatsapp}
							onChange={event => setInputs({ ...inputs, whatsapp: event.target.value })}
							minLength={8}
						/>
					</Form.Group>
				</Col>
				{datas !== undefined ? (
					<Col sm={12} md={6}>
						<Form.Group>
							<Form.Label>
								{t('update_profile.highest_edu')}
								<span className={styles.mandatory}>*</span>
							</Form.Label>
							{/* Add others to the options */}
							<Form.Select
								className={`${styles.formControl} ${techTalentStyles.input}`}
								as='select'
								value={inputs.educationLevel}
								onChange={event => {
									if (event.target.value !== 'OTHERS') {
										setInputs({
											...inputs,
											educationLevel: event.target.value
										})
										// if Others is selected
									} else {
										setInputs({
											...inputs,
											educationLevel: event.target.value,
											education: ''
										})
									}
								}}
								required
							>
								<option value='' disabled>{t('update_profile.select_education_level')}</option>
								{datas.Enum_EducationLevel
									.map((level, index) => (
										<option key={index} value={level.educationLevel}>
											{level.display}
										</option>
									))}
								<option value='OTHERS'>{t('update_profile.others')}</option>
							</Form.Select>
						</Form.Group>
					</Col>
				) : <Col sm={12} md={6}><Spinner animation='border' /></Col>}
				{inputs.educationLevel === 'OTHERS' && (
					<Col sm={12} md={6}>
						<Form.Group>
							<Form.Label>
								{t('update_profile.if_others')}
								<span className={styles.mandatory}>*</span>
							</Form.Label>
							<Form.Control
								className={`${styles.formControl} ${techTalentStyles.input}`}
								type='text'
								value={inputs.education}
								onChange={event => {
									setInputs({
										...inputs,
										education: event.target.value
									})
								}}
								required
							/>
						</Form.Group>
					</Col>
				)}
			</Row>

			<hr className='mt-4 mb-4' />

		</Form>
	)
}
